import { AnimatePresence, motion } from 'framer-motion';
import { Languages, Menu, Moon, Sun, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useI18n, usePreferences } from '@/store/preferences';
import { cx } from '@/lib/format';

const LINKS = [
  { to: '/projects', key: 'projects' },
  { to: '/about', key: 'about' },
  { to: '/certificates', key: 'certificates' },
  { to: '/contact', key: 'contact' },
] as const;

export function Navbar({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  const { t, locale } = useI18n();
  const { theme, toggleTheme, toggleLocale } = usePreferences();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Same rule as ScrollToTop: never let an effect return a non-cleanup value.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // A fixed body prevents the page behind the mobile sheet from scrolling.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const parts = name.split(' ').filter(Boolean);

  // "Jhon Edinson Oliveros Tovar" -> "JT" and "Jhon Tovar": first given name
  // plus last surname, which is how he is addressed anyway.
  const initials =
    parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0]?.slice(0, 2) ?? '';
  const shortName = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : name;

  return (
    <>
      <header
        className={cx(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300 ease-smooth',
          scrolled ? 'glass' : 'border-b border-transparent bg-transparent'
        )}
      >
        <nav className="shell flex h-16 items-center justify-between gap-4 sm:h-20">
          <Link
            to="/"
            className="group flex items-center gap-2.5"
            aria-label={name}
          >
            {/* An uploaded logo replaces the monogram. Separate from the tab
                icon: 16px in a tab strip and 32px next to a name rarely suit
                the same artwork. */}
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                width={32}
                height={32}
                className="h-8 w-8 shrink-0 rounded-plate object-cover"
              />
            ) : (
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-plate border border-line bg-elevated font-mono text-2xs transition-colors group-hover:border-accent group-hover:text-accent">
                {initials}
              </span>
            )}
            {/* Short form only: the full four-part name overflows the bar on
                narrow screens and adds nothing next to the monogram. */}
            <span className="hidden whitespace-nowrap text-xs font-medium md:inline">
              {shortName}
            </span>
          </Link>

          {/* Plain CSS for the active mark. A framer-motion `layoutId` here
              animates between two instances across a route change, and that
              shared-layout transition is what was blanking the page. */}
          <div className="hidden items-center gap-6 md:flex">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cx(
                    'relative py-1 text-xs font-medium transition-colors duration-200',
                    'after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full',
                    'after:transition-transform after:duration-300 after:ease-smooth',
                    isActive
                      ? 'text-ink after:scale-x-100 after:bg-signal'
                      : 'text-muted after:scale-x-0 after:bg-line hover:text-ink hover:after:scale-x-100'
                  )
                }
              >
                {t.nav[link.key]}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleLocale}
              className="btn-icon w-auto gap-1.5 px-3"
              aria-label={t.common.language}
              title={t.common.language}
            >
              <Languages className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">{locale}</span>
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className="btn-icon"
              aria-label={t.common.theme}
              title={t.common.theme}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className="btn-icon md:hidden"
              aria-label={open ? t.nav.close : t.nav.menu}
              aria-expanded={open}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-canvas/95 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="shell flex h-full flex-col justify-center gap-2 pb-20">
              {LINKS.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + index * 0.05, duration: 0.4 }}
                >
                  <NavLink
                    to={link.to}
                    className="h2 block border-b border-line py-5"
                  >
                    {t.nav[link.key]}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
